import moment from 'moment'

import { Dashboard } from '../../models/Dashboard'
import { RecordDataMap } from '../../models/RecordDataMap'
import { Trigger } from '../../models/Trigger'
import { getViewTitleFromView } from '../../utils/dashboard'
import { currencyFormat } from '../../utils/numbers'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../app/design/Table'
import Tag from '../app/design/Tag'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    triggers: RecordDataMap[]
    view: number
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

function TriggerStatus({ trigger }: { trigger?: Trigger }) {
    if (trigger?.pausedDate)
        return <Tag label="Paused" intent="warning" className="w-fit" size="sm" />
    if (trigger?.completedDate)
        return <Tag label="Done" intent="success" className="w-fit" size="sm" />
    if (trigger?.cancelledDate)
        return <Tag label="Cancelled" intent="danger" className="w-fit" size="sm" />
    if (trigger?.startDate && trigger.startDate.isAfter(moment()))
        return <Tag label="Future" intent="muted" className="w-fit" size="sm" />
    return <Tag label="Active" intent="purple" className="w-fit" size="sm" />
}

export function TriggersDashboard({ triggers, view, dashboard, searchText, isLoading }: Props) {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: `${getViewTitleFromView(dashboard, view)} ${
                            triggers.length === 1 ? `smart campaign` : `smart campaigns`
                        }`,
                        value: triggers.length.toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Name</TableHead>
                        <TableHead className="text-right font-normal">Recipients</TableHead>
                        <TableHead className="text-right font-normal">Income</TableHead>
                        <TableHead className="font-normal">Start date</TableHead>
                        <TableHead className="font-normal">Status</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {triggers.map((t) => {
                        const trigger = t.valueForRecordKey<Trigger>('trigger', Trigger)
                        const credits = t.valueForIntKey('credit_count')
                        const income = t.valueForCurrencyKey('income')
                        return (
                            <TableRow key={t.id} className="hover:bg-black/4">
                                <TableCell>
                                    <Label size="sm">{trigger?.label}</Label>
                                </TableCell>
                                <TableCell>
                                    <Paragraph size="sm" className="text-right">
                                        {credits}
                                    </Paragraph>
                                </TableCell>

                                <TableCell>
                                    <Paragraph size="sm" className="text-right">
                                        {currencyFormat(income, true)}
                                    </Paragraph>
                                </TableCell>
                                <TableCell>
                                    <Paragraph size="sm">
                                        {trigger?.startDate?.format('MMM D, YYYY')}
                                    </Paragraph>
                                </TableCell>
                                <TableCell>
                                    <TriggerStatus trigger={trigger} />
                                </TableCell>
                            </TableRow>
                        )
                    })}
                    {triggers.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}
